import React, { useState, useEffect, useRef } from "react";
import * as S from "./stylesV2";
import { matchScore } from "../../services/textSearch";
import { theme } from "../../styles/theme";
import { TaglineGSpan } from "../../styles/stylesTypography";
import Checkbox from "../Checkbox";
import Button from "../button/button_v2";
import NotFound from "../NotFound";

const ChevronIcon = ({ color = "#21262F" }) => (
    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1.5L6 6.5L11 1.5" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// V2 do ButtonFilter: mesmo papel (dropdown de multi-seleção com busca),
// mas com o estilo novo (pilula verde com contador, checkbox custom com
// contagem por opção) e fluxo de "aplicar" - a seleção fica pendente
// dentro do painel e só é confirmada (e o painel fechado) ao clicar em
// APLICAR; LIMPAR só reseta a seleção pendente do painel.
const ButtonFilterV2 = ({
    key_value,
    label,
    options,
    selectedOptions,
    openSelect,
    toggleSelect,
    onApply,
    searchPlaceholder = "Buscar",
    maxHeightOptionsList,
    // Variante compacta (largura mínima menor, conteúdo centralizado) usada
    // em telas/posições apertadas - equivalente ao "justIconFilter" do
    // ButtonFilter original (que, apesar do nome, nunca chegou a renderizar
    // um ícone: só deixava o botão mais estreito).
    justIconFilter = false,
    // Cor da borda quando NÃO há seleção - com seleção continua verde
    // (linguagem visual do V2). Sem isso, cai no cinza padrão do V2.
    borderColor,
    // Padding customizado do botão-gatilho, quando o espaçamento padrão
    // (10px 16px) não serve.
    paddingButtonOthersFilters,
    // Esconde o botão LIMPAR do rodapé, deixando só o APLICAR - pra telas
    // que já têm seu próprio jeito de limpar filtro (ou não querem oferecer).
    showClearFilter = true,
    // Desabilita LIMPAR/APLICAR enquanto uma ação relacionada (ex.: busca)
    // está em andamento.
    loading = false,
}) => {
    const wrapperRef = useRef(null);
    const isOpen = openSelect === key_value;
    const committed = selectedOptions?.[key_value] || [];

    const [pending, setPending] = useState(committed);
    const [searchTerm, setSearchTerm] = useState("");
    const [openUp, setOpenUp] = useState(false);
    const [openLeft, setOpenLeft] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setPending(committed);
            setSearchTerm("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                if (isOpen) toggleSelect(key_value);
            }
        }

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, key_value, toggleSelect]);

    useEffect(() => {
        if (isOpen) {
            const checkPosition = () => {
                if (!wrapperRef.current) return;
                const rect = wrapperRef.current.getBoundingClientRect();
                const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
                const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
                const estimatedDropdownHeight = 320;
                // Painel (S.Panel) tem 230px fixos - se não sobrar isso à
                // direita do gatilho, abre "pra esquerda" (ancorado na borda
                // direita do gatilho, crescendo pra trás) em vez de vazar da
                // tela, mesma ideia do flip vertical logo acima.
                const estimatedPanelWidth = 230;
                setOpenUp((viewportHeight - rect.bottom) < estimatedDropdownHeight);
                setOpenLeft((viewportWidth - rect.left) < estimatedPanelWidth);
            };

            checkPosition();
            window.addEventListener("resize", checkPosition);

            return () => window.removeEventListener("resize", checkPosition);
        } else {
            setOpenUp(false);
            setOpenLeft(false);
        }
    }, [isOpen]);

    // Toggle simples e independente - cada opção (incluindo a "Nacional"/
    // "Todas", quando existir) pode ser marcada junto com qualquer outra,
    // sem nenhuma exclusividade forçada entre elas.
    const toggleOption = (value) => {
        setPending(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
    };

    const handleClear = () => {
        // Limpa de verdade - nenhuma opção fica marcada (nem a "Nacional"/
        // "Todas"). Ao aplicar, um array vazio já é tratado como "sem
        // filtro" por quem consome esse componente.
        setPending([]);
    };

    const handleApply = () => {
        if (onApply) onApply(key_value, pending);
        toggleSelect(key_value);
    };

    // Busca tanto pelo rótulo (ex.: "Goiás") quanto pelo value (ex.: "GO") -
    // sem isso, opções cujo value é a sigla e o rótulo é o nome por extenso
    // só apareciam se a sigla batesse por acaso com o começo do nome.
    const list = (options?.[key_value] || []).filter(
        (option) => matchScore(searchTerm, [option?.label || "", option?.value ?? ""]) !== null
    );

    return (
        <S.Wrapper ref={wrapperRef}>
            <S.TriggerButton
                type="button"
                hasSelection={committed.length > 0}
                compact={justIconFilter}
                borderColor={borderColor}
                customPadding={paddingButtonOthersFilters}
                onClick={() => toggleSelect(key_value)}
            >
                <TaglineGSpan color={committed.length > 0 ? theme.colors.green.Verde_AgroReceita : theme.colors.gray.Cinza_2}>
                    {label}
                </TaglineGSpan>

                {committed.length > 0 && <S.Badge>{committed.length}</S.Badge>}
                
                <S.Chevron open={isOpen}>
                    <ChevronIcon color={committed.length > 0 ? theme.colors.green.Verde_AgroReceita : "#21262F"} />
                </S.Chevron>
            </S.TriggerButton>

            {isOpen && (
                <S.Panel openUp={openUp} openLeft={openLeft}>
                    <S.SearchInput
                        type="text"
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                    />

                    <S.OptionsList maxHeight={maxHeightOptionsList}>
                        {list.length === 0 && (
                            <NotFound
                                message="Nenhum resultado encontrado."
                                iconSize="40px"
                                textSize="0.5rem"
                                gap="8px"
                            />
                        )}

                        {list.map((option, i) => {
                            const checked = pending.includes(option.value);

                            return (
                                <S.OptionRow key={`${key_value}_${i}`} onClick={() => toggleOption(option.value)}>
                                    <S.OptionLeft>
                                        <Checkbox checked={checked} />
                                        <S.OptionLabel checked={checked}>{option.label}</S.OptionLabel>
                                    </S.OptionLeft>

                                    {(option.count !== undefined && option.count !== null) && (
                                        <S.OptionCount>{option.count}</S.OptionCount>
                                    )}
                                </S.OptionRow>
                            );
                        })}
                    </S.OptionsList>

                    <S.Footer>
                        {showClearFilter && (
                            <S.FooterButtonWrap>
                                <Button
                                    width="100%"
                                    height="32px"
                                    value="LIMPAR"
                                    outline={true}
                                    color={theme.colors.gray.Cinza_2}
                                    disabled={loading}
                                    onClick={handleClear}
                                />
                            </S.FooterButtonWrap>
                        )}

                        <S.FooterButtonWrap>
                            <Button
                                width="100%"
                                height="32px"
                                value="APLICAR"
                                outline={false}
                                disabled={loading}
                                onClick={handleApply}
                            />
                        </S.FooterButtonWrap>
                    </S.Footer>
                </S.Panel>
            )}
        </S.Wrapper>
    );
};

export default ButtonFilterV2;
